/*
 * When the session expires mid-visit (any authenticated request returns 401), this dialog asks for the
 * password (and the 2FA code if on) and signs back in without leaving the page; every query then refetches.
 * Mounted once in the root layout. Test it from /dev/kit ("Expire session").
 */
import { LockKey } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { api, isApiError } from '@/api/client'
import { useSession } from '@/api/hooks/auth'
import { authToken, sessionEvents } from '@/api/token'
import type { AuthToken, SignInResult } from '@/api/types'
import { Alert, Bidi, Button, Dialog, Field, OtpInput, PasswordInput } from '@/components/ui'
import { isMock } from '@/lib/env'
import { problemMessage } from '@/lib/errors'

export function SessionExpiredDialog() {
  const { t } = useTranslation('auth')
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [challenge, setChallenge] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => sessionEvents.onExpired(() => setOpen(true)), [])
  // the session itself coming back 401 while we still hold the last good one (any page, shell or not)
  const session = useSession()
  const email = session.data?.user.email ?? ''
  const sessionExpired = Boolean(session.data) && isApiError(session.error) && session.error.status === 401
  const isOpen = open || sessionExpired

  const finish = async (tk: AuthToken) => {
    authToken.set(tk.token)
    setOpen(false)
    setPassword('')
    setCode('')
    setChallenge(null)
    setError(null)
    await qc.invalidateQueries()
  }
  const submit = async (otp: string = code) => {
    setBusy(true)
    setError(null)
    try {
      if (challenge) {
        await finish(
          await api.post<AuthToken>(
            '/auth/login/totp',
            { challengeId: challenge, code: otp },
            { anonymous: true },
          ),
        )
      } else {
        const r = await api.post<SignInResult>('/auth/login', { email, password }, { anonymous: true })
        if (r.status === 'totp_required') setChallenge(r.challengeId)
        else await finish(r)
      }
    } catch (e) {
      setError(e)
      setCode('')
    } finally {
      setBusy(false)
    }
  }
  const signOut = () => {
    authToken.clear()
    qc.clear()
    setOpen(false)
    navigate('/login', { replace: true })
  }
  const msg = error ? problemMessage(error) : null

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setOpen}
      dismissible={false}
      size="sm"
      title={
        <span className="flex items-center gap-2.5">
          <LockKey className="size-5 text-brand-600" weight="duotone" aria-hidden="true" />
          {t('expired.title')}
        </span>
      }
      description={t('expired.body')}
      footer={
        <>
          <Button variant="ghost" onClick={signOut} disabled={busy}>
            {t('expired.signOut')}
          </Button>
          <Button
            type="submit"
            form="session-expired-form"
            loading={busy}
            disabled={challenge ? code.length !== 6 : !password}
          >
            {challenge ? t('totp.submit') : t('expired.submit')}
          </Button>
        </>
      }
    >
      <form
        id="session-expired-form"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
        className="flex flex-col gap-4"
      >
        {msg ? (
          <Alert tone="danger" title={msg.title}>
            {msg.detail}
          </Alert>
        ) : null}
        <div className="rounded-xl bg-surface-soft px-3 py-2.5 text-14 ring-1 ring-hairline">
          <bdi dir="ltr" className="font-medium text-ink">
            {email}
          </bdi>
        </div>
        {challenge ? (
          <Field label={t('totp.codeLabel')}>
            <OtpInput value={code} onChange={setCode} onComplete={(v) => void submit(v)} autoFocus />
          </Field>
        ) : (
          <Field label={t('fields.password')}>
            <PasswordInput
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}
        {isMock ? (
          <p className="text-12 text-subtle">
            <Bidi
              text={
                challenge
                  ? t('totp.mockHint', { code: '123456' })
                  : t('signIn.mockHint', { password: 'bpanel-demo-2026' })
              }
            />
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}
